import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, CircularProgress, Box, TextField,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import { useTranslation } from 'react-i18next';
import myDriveService, { DriveFile, DirCategory } from '../services/myDriveService';
import { useMessage } from '../../../../contexts/MessageContext';

interface Props {
  open: boolean;
  onClose: () => void;
  onPick: (targetRelativePath: string) => void;
  dirCategory: DirCategory | string;
  excludeFileId: number;
}

function buildTree(folders: DriveFile[], parentPath: string): DriveFile[] {
  return folders.filter(f => {
    const parent = f.relative_path.split('/').slice(0, -1).join('/');
    return parent === parentPath;
  });
}

interface TreeNodeProps {
  folder: DriveFile;
  allFolders: DriveFile[];
  excludeId: number;
  selected: string;
  onSelect: (path: string) => void;
  depth: number;
}

function TreeNode({ folder, allFolders, excludeId, selected, onSelect, depth }: TreeNodeProps) {
  const children = buildTree(allFolders, folder.relative_path).filter(f => f.id !== excludeId);
  const isSelected = selected === folder.relative_path;

  return (
    <>
      <ListItemButton
        selected={isSelected}
        onClick={() => onSelect(folder.relative_path)}
        sx={{ pl: 2 + depth * 2 }}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          {isSelected ? <FolderOpenIcon color="primary" fontSize="small" /> : <FolderIcon fontSize="small" />}
        </ListItemIcon>
        <ListItemText primary={folder.filename} primaryTypographyProps={{ variant: 'body2' }} />
      </ListItemButton>
      {children.map(child => (
        <TreeNode key={child.id} folder={child} allFolders={allFolders}
                  excludeId={excludeId} selected={selected} onSelect={onSelect}
                  depth={depth + 1} />
      ))}
    </>
  );
}

export default function FolderTreePicker({ open, onClose, onPick, dirCategory, excludeFileId }: Props) {
  const { t } = useTranslation();
  const { showApiError } = useMessage();
  const [folders,        setFolders]        = useState<DriveFile[]>([]);
  const [loading,        setLoading]        = useState(false);
  const [selected,       setSelected]       = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName,  setNewFolderName]  = useState('');

  const loadFolders = useCallback(() => {
    if (!dirCategory) return;
    setLoading(true);
    myDriveService.listFiles(dirCategory as DirCategory)
      .then(data => setFolders(
        data.items.filter(f => f.is_directory && f.id !== excludeFileId)
      ))
      .catch(() => setFolders([]))
      .finally(() => setLoading(false));
  }, [dirCategory, excludeFileId]);

  useEffect(() => {
    if (!open) return;
    setSelected('');
    setCreatingFolder(false);
    setNewFolderName('');
    loadFolders();
  }, [open, loadFolders]);

  const handleCreateSubfolder = async () => {
    if (!newFolderName.trim()) return;
    const parent = selected;
    const newPath = parent ? `${parent}/${newFolderName.trim()}` : newFolderName.trim();
    try {
      await myDriveService.createFolder(dirCategory as DirCategory, newPath);
      await loadFolders();
      setSelected(newPath);
      setCreatingFolder(false);
      setNewFolderName('');
    } catch (err) {
      showApiError(err);
    }
  };

  const rootFolders = buildTree(folders, '');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('myDrive.moveDialogTitle', '移動到...')}</DialogTitle>
      <DialogContent dividers sx={{ p: 0, minHeight: 200 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List dense disablePadding>
            <ListItemButton selected={selected === ''} onClick={() => setSelected('')} sx={{ pl: 2 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <FolderIcon fontSize="small" color={selected === '' ? 'primary' : undefined} />
              </ListItemIcon>
              <ListItemText primary={t('myDrive.root', '根目錄')} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItemButton>
            {rootFolders.map(f => (
              <TreeNode key={f.id} folder={f} allFolders={folders}
                        excludeId={excludeFileId} selected={selected}
                        onSelect={setSelected} depth={0} />
            ))}
            {!loading && folders.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, display: 'block' }}>
                {t('myDrive.moveTargetEmpty', '（請選擇目標資料夾）')}
              </Typography>
            )}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {/* 新建子目錄 */}
        {!creatingFolder ? (
          <Button size="small" startIcon={<CreateNewFolderIcon />}
                  onClick={() => setCreatingFolder(true)}
                  sx={{ mr: 'auto' }}>
            {t('myDrive.newSubfolder', '新建子目錄')}
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mr: 'auto' }}>
            <TextField
              size="small"
              placeholder={t('myDrive.newFolderPlaceholder', '資料夾名稱')}
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateSubfolder()}
              sx={{ width: 130 }}
              autoFocus
            />
            <Button size="small" onClick={handleCreateSubfolder} disabled={!newFolderName.trim()}>
              {t('common.confirm', '確定')}
            </Button>
            <Button size="small" onClick={() => { setCreatingFolder(false); setNewFolderName(''); }}>
              {t('common.cancel', '取消')}
            </Button>
          </Box>
        )}
        <Button onClick={onClose}>{t('myDrive.moveCancel', '取消')}</Button>
        <Button variant="contained" onClick={() => { onPick(selected); }}>
          {t('myDrive.moveConfirm', '移動')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
